import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Send } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { downloadFile, formatGBP2 } from "../../../components/Features";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { ConfirmDialog, eventText, eventTone, fmtDate, fmtDateTime, MarkPaidDialog, saGet, saPost, StatusChip, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

const InvoiceDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, showMessage, closeToast] = useToast();

  const { websiteData } = useWebsite();
  useSiteBranding(data?.invoice?.invoice_number || "Invoice", websiteData);

  const load = () => {
    saGet(`invoices/${id}`).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") setData(response.data.data);
      else showMessage(response.data.message, "error");
    }).catch(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id]);

  const handle = (response) => {
    setBusy(false);
    showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
    if (response.data.status === "SUCCESS") load();
  };

  const inv = data?.invoice;
  const company = data?.company;
  const issuer = data?.issuer || {};
  const open = inv && (inv.status === "pending" || inv.status === "overdue");

  return (<div className={`${styles.Page}`}>
    <PageHeader title={inv?.invoice_number || "Invoice"} trail={[{ label: "Invoices", to: "/super-admin/invoices" }]} />

    {loading && <TableSkeleton rows={5} />}

    {!loading && inv && (<div className={`${styles.Grid2}`}>
      <div className={`${styles.InvoiceDoc}`}>
        <div className={`${styles.InvoiceTop}`}>
          <div>
            <p className={`${styles.InvoiceNumber}`}>{inv.invoice_number}</p>
            <StatusChip status={inv.status} />
          </div>
          <div style={{ textAlign: "right", fontSize: 13, color: "#4b5563", lineHeight: "20px" }}>
            <strong style={{ color: "#0f1b33" }}>{issuer.billing_company_name}</strong><br />
            {issuer.billing_address && <span style={{ whiteSpace: "pre-line" }}>{issuer.billing_address}<br /></span>}
            {issuer.billing_email}
            {issuer.billing_vat_no && <><br />VAT {issuer.billing_vat_no}</>}
          </div>
        </div>

        <div className={`${styles.GridHalf}`} style={{ marginBottom: 20 }}>
          <div>
            <span className={`${styles.Muted}`}>Bill to</span>
            <p style={{ margin: "4px 0 0", fontSize: 14, lineHeight: "21px" }}>
              <Link to={`/super-admin/companies/${company?.id}`} className={`${styles.RowLink}`}>{company?.company_name}</Link><br />
              {company?.name}<br />
              {company?.email}
            </p>
          </div>
          <dl className={`${styles.DefList}`}>
            <div><dt>Issued</dt><dd>{fmtDate(inv.issue_date)}</dd></div>
            <div><dt>Due</dt><dd>{fmtDate(inv.due_date)}</dd></div>
            <div><dt>Period</dt><dd>{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</dd></div>
            <div><dt>{inv.status === "paid" ? "Paid" : "Suspends after"}</dt><dd>{inv.status === "paid" ? fmtDate(inv.paid_at) : fmtDate(inv.suspend_on)}</dd></div>
          </dl>
        </div>

        <table className={`${styles.LineTable}`}>
          <thead><tr><th>Description</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
          <tbody>
            {(inv.items || []).map((item) => (<tr key={item.id}><td>{item.description}</td><td style={{ textAlign: "right" }}>{formatGBP2(item.amount)}</td></tr>))}
            <tr className={`${styles.LineTotal}`}><td>Total ({inv.currency})</td><td style={{ textAlign: "right" }}>{formatGBP2(inv.total)}</td></tr>
          </tbody>
        </table>

        {inv.status === "paid" && <p className={`${styles.Muted}`} style={{ marginTop: 16 }}>Paid {fmtDate(inv.paid_at)}{inv.payment_method ? ` by ${inv.payment_method}` : ""}{inv.payment_reference ? ` · ref ${inv.payment_reference}` : ""}</p>}
      </div>

      <div>
        <div className={`${styles.Panel}`} style={{ marginBottom: 18 }}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 14 }}>Actions</p>
          <div className={`${styles.Actions}`} style={{ justifyContent: "flex-start" }}>
            {open && <button type="button" className={`${styles.ActionBU} ${styles.ActionPrimary}`} onClick={() => setPayOpen(true)}>Mark paid</button>}
            <button type="button" className={`${styles.ActionBU}`} onClick={() => saPost(`invoices/${inv.id}/resend`).then(handle)}><Send />Resend email</button>
            <button type="button" className={`${styles.ActionBU}`} onClick={() => downloadFile(`v1/admin/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`)}><Download />PDF</button>
            {open && <button type="button" className={`${styles.ActionBU} ${styles.ActionDanger}`} onClick={() => setVoidOpen(true)}>Void</button>}
          </div>
          <p className={`${styles.Muted}`} style={{ marginTop: 14 }}>
            Email: {inv.emailed_at ? `sent ${fmtDateTime(inv.emailed_at)}` : inv.email_error ? <span className={`${styles.Danger}`}>failed — {inv.email_error}</span> : "not sent"}
          </p>
        </div>

        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 14 }}>History</p>
          {(data.events || []).length === 0 && <p className={`${styles.EmptyNote}`}>No history yet.</p>}
          <ul className={`${styles.Timeline}`}>
            {(data.events || []).map((e) => (<li key={e.id} className={eventTone(e.type)}>
              <span className={`${styles.TimelineText}`}>{eventText(e)}</span>
              <span className={`${styles.SubText}`}>{e.actor_name || "System"} · {fmtDateTime(e.created_at)}</span>
            </li>))}
          </ul>
        </div>
      </div>
    </div>)}

    <MarkPaidDialog invoice={payOpen ? { ...inv, company_name: company?.company_name } : null} onClose={() => setPayOpen(false)} onDone={load} showMessage={showMessage} />
    <ConfirmDialog open={voidOpen} busy={busy} title={`Void ${inv?.invoice_number}?`} message="The invoice is cancelled and no longer counts as owed. If it was the company's only overdue invoice, an overdue suspension is lifted." confirmLabel="Void invoice" onConfirm={() => { setBusy(true); saPost(`invoices/${inv.id}/void`).then((r) => { setVoidOpen(false); handle(r); }); }} onClose={() => setVoidOpen(false)} />
    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(InvoiceDetail);
